// OP1WebButtonAppWindow (build review SEP.23.2026; formerly
// AppWindowButton): connects a dock item to an app window through the
// Desktop State Manager System. It never touches a window DOM element
// directly - it only sends an action to shared Desktop state.
//
// No slot (per the build review): the icon is a prop, and the look lives in
// ONE place, OP1WebButtonAppWindow.module.css. Every instance in every
// Plasmic page picks up a design change from that file automatically, so
// nothing has to be copy-pasted or redesigned per instance.
//
// Variant `state` (camelCase, three values):
//   "closed" - the window is not open                      (no indicator)
//   "open"   - the window is open but not the active one   (small dot)
//   "active" - the window is open, focused and not minimized (wide bar)
// Minimized windows count as "open": they're running, just not in front.
import React, { useEffect } from "react";
import { useDesktopState, useWindowState } from "./DesktopStateProvider";
import styles from "./OP1WebButtonAppWindow.module.css";

export const BUTTON_STATES = ["closed", "open", "active"];

const OP1WebButtonAppWindow = React.forwardRef(function OP1WebButtonAppWindow(
  {
    windowId,
    className,
    icon,
    iconAlt,
    previewState,
    onOpenChange,
    onFocusedChange,
    onStateChange,
  },
  ref
) {
  const { actions } = useDesktopState();
  const win = useWindowState(windowId);
  const isOpen = !!win?.isOpen;
  const isFocused = !!win?.isFocused;
  const isMinimized = !!win?.isMinimized;

  let liveState = "closed";
  if (isOpen) liveState = isFocused && !isMinimized ? "active" : "open";
  // `previewState` exists only so designers can see each variant in Studio's
  // canvas (where there's no real window to open). Leave it unset in real
  // pages and the button follows the live window state.
  const state = BUTTON_STATES.includes(previewState) ? previewState : liveState;

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    onFocusedChange?.(isFocused);
  }, [isFocused, onFocusedChange]);

  useEffect(() => {
    onStateChange?.(state);
  }, [state, onStateChange]);

  const handleClick = () => {
    if (!windowId) return;
    if (!isOpen) {
      actions.openWindow(windowId);
    } else if (isMinimized) {
      actions.restoreWindow(windowId);
    } else if (isFocused) {
      actions.minimizeWindow(windowId);
    } else {
      actions.focusWindow(windowId);
    }
  };

  return (
    <button
      ref={ref}
      type="button"
      className={[styles.button, className].filter(Boolean).join(" ")}
      data-state={state}
      data-open={isOpen}
      data-focused={isFocused}
      aria-pressed={state === "active"}
      onClick={handleClick}
    >
      {icon ? (
        <img
          src={icon}
          alt={iconAlt || ""}
          className={styles.icon}
          draggable={false}
        />
      ) : null}
      <span className={styles.indicator} aria-hidden="true" />
    </button>
  );
});

export default OP1WebButtonAppWindow;
